import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarV2, calendarV2Api } from "../calendar-v2";
import "./CalendarV2Preview.css";
import { useVisibilityRefetch } from "../hooks/useVisibilityRefetch";
import GoogleProviderReadiness from "../components/instructor/GoogleProviderReadiness";

const explain = (reason) => ({
  pupil_not_found: "That pupil is not an active pupil in the clean pupil list.",
  pupil_address_incomplete: "Add the pupil’s permanent address before booking.",
  space_already_booked: "That space has just been taken.",
  insufficient_travel_from_previous: "There is not enough travel time after the previous lesson.",
  insufficient_travel_to_next: "There is not enough travel time before the next lesson.",
  travel_provider_unavailable: "Google could not confirm the journey right now.",
}[reason] || String(reason || "The booking cannot be made.").replaceAll("_", " "));

export default function CalendarV2Preview() {
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);
  const [assignment, setAssignment] = useState(null);
  const [pupils, setPupils] = useState([]);
  const [panelState, setPanelState] = useState("idle");
  const [message, setMessage] = useState("");
  const [approval, setApproval] = useState(null);
  const [repeat, setRepeat] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");

  const refreshCalendar = useCallback(() => {
    setReloadKey((value) => value + 1);
  }, []);
  useVisibilityRefetch(refreshCalendar, {
    enabled: !assignment && !repeat,
    marker: "calendar",
  });

  useEffect(() => {
    if (!assignment) return;
    let active = true;
    setPanelState("loading");
    setMessage("");
    calendarV2Api.pupils()
      .then((result) => {
        if (!active) return;
        setPupils(result.pupils || []);
        setPanelState("ready");
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error.message);
        setPanelState("error");
      });
    return () => { active = false; };
  }, [assignment]);

  const closePanel = () => {
    setAssignment(null);
    setApproval(null);
    setMessage("");
    setPanelState("idle");
  };

  const commit = async (pupil, instructorApproved = false) => {
    const payload = {
      pupil_id: pupil.id,
      date: assignment.date,
      start: assignment.space.start,
      end: assignment.space.end,
      instructor_approved: instructorApproved,
    };
    setPanelState("working");
    setMessage("Checking the pupil, journey and neighbouring lessons…");
    try {
      const plan = await calendarV2Api.planBooking(payload);
      if (plan.decision === "rejected") {
        setMessage(plan.reasons.map(explain).join(" "));
        setPanelState("ready");
        return;
      }
      if (plan.requires_instructor_approval && !instructorApproved) {
        setApproval({ pupil, plan });
        setMessage(plan.reasons.map(explain).join(" "));
        setPanelState("approval");
        return;
      }
      await calendarV2Api.commitBooking(payload);
      closePanel();
      setReloadKey((value) => value + 1);
    } catch (error) {
      const reasons = error.payload?.detail?.reasons;
      setMessage(Array.isArray(reasons) ? reasons.map(explain).join(" ") : error.message);
      setPanelState("ready");
    }
  };

  const saveSlot = ({ error }) => {
    setSaveMessage(error || "Booking space saved for this day");
    window.setTimeout(() => setSaveMessage(""), 2600);
  };

  const commitRepeat = async (instructorApproved = false) => {
    if (!repeat?.space?.pupil_id) return;
    setRepeat((current) => ({ ...current, state: "committing" }));
    try {
      const result = await calendarV2Api.commitRepeat({
        pupil_id: repeat.space.pupil_id,
        first_date: repeat.date,
        start: repeat.space.start,
        end: repeat.space.end,
        occurrences: 10,
        confirmed: true,
        instructor_approved: instructorApproved,
      });
      setRepeat((current) => ({ ...current, state: "complete", result }));
      setReloadKey((value) => value + 1);
    } catch (error) {
      const detail = error.payload?.detail;
      if (detail?.requires_instructor_approval && !instructorApproved) {
        setRepeat((current) => ({
          ...current,
          state: "approval",
          message: (detail.reasons || []).map(explain).join(" "),
        }));
        return;
      }
      setRepeat((current) => ({
        ...current,
        state: "error",
        message: explain(detail?.reason) || error.message,
      }));
    }
  };

  const proposeRepeat = async ({ date, space }) => {
    if (!space.pupil_id) return;
    setRepeat({ state: "loading", date, space });
    try {
      const proposal = await calendarV2Api.proposeRepeat({
        pupil_id: space.pupil_id,
        first_date: date,
        start: space.start,
        end: space.end,
        occurrences: 10,
      });
      setRepeat({ state: "ready", date, space, proposal });
    } catch (error) {
      setRepeat({ state: "error", date, space, message: error.message });
    }
  };

  return (
    <main className="cv2-preview">
      <div className="cv2-preview__notice">
        <strong>Calendar</strong>
        <span>Calendar, Run My Day and ChatGPT use the same live bookings.</span>
      </div>

      <div className="cv2-preview__diagnostic">
        <GoogleProviderReadiness />
      </div>

      <CalendarV2
        key={reloadKey}
        onAssign={setAssignment}
        onOpenBooking={({ space }) => space.pupil_id && navigate(`/pupil/${space.pupil_id}`)}
        onRepeat={proposeRepeat}
        onSlotChange={saveSlot}
      />
      {saveMessage && <p className="cv2-preview__save" role="status">{saveMessage}</p>}

      {assignment && (
        <section className="cv2-panel" aria-label="Assign pupil">
          <header>
            <div>
              <span>Assign pupil</span>
              <strong>{assignment.space.start}–{assignment.space.end}</strong>
            </div>
            <button type="button" onClick={closePanel}>Close</button>
          </header>
          {panelState === "loading" && <p>Loading active pupils…</p>}
          {panelState !== "loading" && pupils.length === 0 && (
            <div className="cv2-panel__empty">
              <strong>No active pupils yet</strong>
              <p>Only active pupils linked to you are available for booking.</p>
            </div>
          )}
          <div className="cv2-panel__pupils">
            {pupils.map((pupil) => (
              <button type="button" key={pupil.id} disabled={panelState === "working"} onClick={() => commit(pupil)}>
                <span>{pupil.name}</span><span>Check and book</span>
              </button>
            ))}
          </div>
          {message && <p className="cv2-panel__message">{message}</p>}
          {approval && (
            <button type="button" className="cv2-panel__approve" onClick={() => commit(approval.pupil, true)}>
              Approve this change
            </button>
          )}
        </section>
      )}

      {repeat && (
        <section className="cv2-panel" aria-label="Repeat booking proposal">
          <header>
            <div><span>Repeat same day and time</span><strong>Next 10 occurrences</strong></div>
            <button type="button" onClick={() => setRepeat(null)}>Close</button>
          </header>
          {repeat.state === "loading" && <p>Checking genuine availability…</p>}
          {repeat.state === "committing" && <p>Rechecking every journey and booking the available set…</p>}
          {repeat.state === "error" && <p className="cv2-panel__message">{repeat.message}</p>}
          {repeat.state === "ready" && (
            <>
              <p><strong>{repeat.proposal.available_dates.length}</strong> spaces available; <strong>{repeat.proposal.unavailable_dates.length}</strong> skipped.</p>
              <p className="cv2-panel__message">Nothing changes until you confirm this set.</p>
              <button type="button" className="cv2-panel__approve" onClick={() => commitRepeat(false)}>
                Confirm repeat bookings
              </button>
            </>
          )}
          {repeat.state === "approval" && (
            <>
              <p className="cv2-panel__message">{repeat.message}</p>
              <button type="button" className="cv2-panel__approve" onClick={() => commitRepeat(true)}>
                Approve and book the set
              </button>
            </>
          )}
          {repeat.state === "complete" && (
            <p><strong>{repeat.result.booked_dates.length}</strong> bookings added; <strong>{repeat.result.skipped_dates.length}</strong> unavailable dates skipped.</p>
          )}
        </section>
      )}
    </main>
  );
}
